/**
 * Le RNG déterministe du moteur — SPEC-MOTEUR §3.
 *
 * `draw` est LE point d'entrée unique de l'aléa : aucun `Math.random` n'existe dans
 * `shared/` (CLAUDE.md). Même `(seed, turn, purpose, index)` ⇒ même tirage, toujours
 * (anti-save-scum, §3.3) : rejouer la même action au même turn redonne exactement le même
 * résultat, et tout choix différent consomme d'autres tirages — la run diverge.
 *
 * `turn` n'est incrémenté que par une action **acceptée** (§2.3).
 */
/** Valeurs runtime des purposes — sert aux gardes et aux tests d'exhaustivité. */
export const RNG_PURPOSES = [
    "chance_roll",
    "viral_roll",
    "event_pick",
    "event_choice_outcome",
    "event_params",
    "trend_rotation",
    "inspiration_draw",
    "atelier_pattern",
    "jalon_params",
    "pnj_pick",
    "fil_draw",
    "fil_outcome",
    "manager_choice",
];
// ─────────────────────────────────────────────────────────────────────────────
// §3.1 Algorithme exact — code normatif, recopié tel quel
// ─────────────────────────────────────────────────────────────────────────────
function xmur3(str) {
    let h = 1779033703 ^ str.length;
    for (let i = 0; i < str.length; i++) {
        h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
        h = (h << 13) | (h >>> 19);
    }
    return function () {
        h = Math.imul(h ^ (h >>> 16), 2246822507);
        h = Math.imul(h ^ (h >>> 13), 3266489909);
        return (h ^= h >>> 16) >>> 0;
    };
}
function mulberry32(a) {
    return function () {
        a |= 0;
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
/**
 * LE point d'entrée unique de l'aléa du moteur. Retourne u ∈ [0;1[.
 *
 * `index` sert quand un même purpose tire plusieurs valeurs dans le même turn
 * (ex. 3 cartes d'inspiration → index 0, 1, 2). Un tirage à N issues pondérées
 * consomme **un seul** `draw` (§3.1).
 */
export function draw(seed, turn, purpose, index = 0) {
    return mulberry32(xmur3(`${seed}:${turn}:${purpose}:${index}`)())();
}
// ─────────────────────────────────────────────────────────────────────────────
// Helpers dérivés — tous bâtis sur `draw`, aucun aléa supplémentaire
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Sélection par roulette sur des poids cumulés — **un seul** `draw` (§10.1 pas 6).
 * Retourne l'index choisi, ou −1 si la somme des poids est nulle ou négative.
 */
export function pickWeighted(weights, u) {
    let total = 0;
    for (const w of weights)
        if (w > 0)
            total += w;
    if (total <= 0)
        return -1;
    let cursor = u * total;
    for (let i = 0; i < weights.length; i++) {
        const w = weights[i];
        if (w <= 0)
            continue;
        cursor -= w;
        if (cursor < 0)
            return i;
    }
    // Garde-fou d'arrondi flottant : le dernier poids strictement positif gagne.
    for (let i = weights.length - 1; i >= 0; i--)
        if (weights[i] > 0)
            return i;
    return -1;
}
/** Tirage uniforme d'un entier dans [min;max] (bornes incluses) à partir d'un `u`. */
export function uniformInt(u, min, max) {
    if (max <= min)
        return min;
    return min + Math.floor(u * (max - min + 1));
}
/** Tirage uniforme d'un réel dans [min;max[ à partir d'un `u`. */
export function uniformFloat(u, min, max) {
    return min + u * (max - min);
}
//# sourceMappingURL=rng.js.map